import {
  Component,
  inject,
  ElementRef,
  AfterViewChecked,
  OnDestroy,
  viewChildren
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastService } from '../../core/services/toast.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { IconNameType } from '../icons/icon-name-type.enum';
import { IconComponent } from "../icons/icon.component";

@Component({
  selector: 'cwo-toast',
  standalone: true,
  imports: [CommonModule, IconComponent],
  templateUrl: './toast.component.html',
  styleUrl: './toast.component.scss',
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({
          transform: 'translateY(-150%)',
                    opacity: 0
        }),
                animate('400ms cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          style({
            transform: 'translateY(0)',
                        opacity: 1
          })
                )
      ]),
      transition(':leave', [
        style({
          transform: 'translateY(0)',
                    opacity: 1
        }),
                animate('400ms cubic-bezier(0.6, -0.28, 0.735, 0.045)',
          style({
            transform: 'translateY(-150%)',
                        opacity: 0
          })
                )
            ])
        ])
    ]
})
export class ToastComponent implements AfterViewChecked, OnDestroy {
  toastService = inject(ToastService);
  IconNameType = IconNameType;

  readonly toastElements = viewChildren<ElementRef<HTMLElement>>('toastElement');
  readonly closeButtons = viewChildren<ElementRef<HTMLButtonElement>>('closeButton');

  private previousToastCount = 0;
  private focusTimeoutRefs: number[] = [];

  ngAfterViewChecked() {
    const currentCount = this.toastService.toastQueue().length;

    if (currentCount > this.previousToastCount) {
      // Wait for animation/rendering to complete
      const timeoutId = window.setTimeout(() => {
        this.focusLatestToast();
      }, 100);

      this.focusTimeoutRefs.push(timeoutId);
    }

    this.previousToastCount = currentCount;
  }

  ngOnDestroy() {
    this.focusTimeoutRefs.forEach((timeoutId) => clearTimeout(timeoutId));
  }

  private focusLatestToast() {
    const toasts = this.toastElements();

    if (toasts.length > 0) {
      const latestToast = toasts[toasts.length - 1];
      latestToast.nativeElement.focus();
    }
  }

  onClose(toastId?: string) {
    if (toastId) {
      this.toastService.hideToast(toastId);
    } else {
      this.toastService.hide();
    }

    // Return focus to the previously focused element when toast is closed
    this.restoreFocus();
  }

  private restoreFocus() {
    // If there are still toasts, focus the latest one
    if (this.toastService.toastQueue().length > 0) {
      setTimeout(() => this.focusLatestToast(), 50);
    }
  }

  getIconName(type?: string) {
    switch (type) {
      case 'success':
        return IconNameType.check;
      case 'error':
        return IconNameType.error;
      default:
        return IconNameType.check;
    }
  }
}